import { withScrollRestore } from '@monofe/ui-common';
import { m } from 'framer-motion';
import type { RefObject } from 'react';
import { forwardRef, memo, useCallback, useEffect } from 'react';
import isEqual from 'react-fast-compare';
import { useInView } from 'react-intersection-observer';
import useLocationPath from '../../common/useLocationPath';
import ItemView from './ItemView';
import {
    StyleElement,
    classNameForElement,
    initializeStaticStyling,
    styleForElement,
} from './ListView.style';
import type { IHamburgerCollectionViewProperties, IHamburgerItemViewProperties } from './interface';

function ItemRowView(properties: {
    item: IHamburgerItemViewProperties;
    main: IHamburgerCollectionViewProperties;
    saveCurrentScrollPosition?: () => void;
}): JSX.Element {
    const style = useCallback(
        (element: StyleElement) => styleForElement(element, properties.main),
        [properties],
    );
    const className = useCallback(
        (element: StyleElement) => classNameForElement(element, properties.main),
        [properties],
    );
    const onShow = useCallback(() => properties.item.onShow?.(properties.item), [properties.item]);
    const { ref, inView } = useInView({});

    useEffect(() => {
        if (inView) {
            onShow();
        }
    }, [inView, onShow]);

    return (
        <section
            ref={ref}
            className={className(StyleElement.ItemContainer)}
            style={style(StyleElement.ItemContainer)}
        >
            <ItemView
                {...properties.item}
                saveCurrentScrollPosition={properties.saveCurrentScrollPosition}
            />
        </section>
    );
}

const MemoContainerItemView = memo(ItemRowView);

const _InnerContainerView = forwardRef<HTMLDivElement, IHamburgerCollectionViewProperties>(
    (properties: IHamburgerCollectionViewProperties, reference) => {
        const style = (element: StyleElement) => styleForElement(element, properties);
        const className = (element: StyleElement) => classNameForElement(element, properties);

        return (
            <m.nav
                ref={reference}
                className={className(StyleElement.Container)}
                style={style(StyleElement.Container)}
            >
                {properties.items.map((model, index) => (
                    <MemoContainerItemView key={index} item={model} main={properties} />
                ))}
            </m.nav>
        );
    },
);

const ScrollRestoreInnerContainerView = withScrollRestore(_InnerContainerView);
function View(
    properties: IHamburgerCollectionViewProperties & {
        refObject?: RefObject<HTMLDivElement> | null;
    },
): JSX.Element {
    initializeStaticStyling();
    const locationPath = useLocationPath();

    return (
        <ScrollRestoreInnerContainerView
            saveKey={`HamburgerListView${locationPath}`}
            {...properties}
        />
    );
}

function propertiesEqual(
    previous: IHamburgerCollectionViewProperties,
    next: IHamburgerCollectionViewProperties,
): boolean {
    const match = isEqual(previous, next);
    return match;
}

export default memo(View, propertiesEqual);
